/* eslint-env jest */
import {
  parseItem, collapseItems, isDownloadItem, hasPhysicalItem,
  isClassPictureItem, classPictureIsTheOnlyPrint,
} from './packageItems';

describe('isDownloadItem', () => {
  it('matches download rows', () => {
    expect(isDownloadItem('(11) High-Resolution Downloads')).toBe(true);
    expect(isDownloadItem('(1) Download')).toBe(true);
  });

  it('matches "digital" rows too, as the server ImageType.downloadable scope does', () => {
    expect(isDownloadItem('(2) Digital Images')).toBe(true);
    expect(isDownloadItem('(4) DIGITAL IMAGES')).toBe(true);
  });

  it('does not match prints', () => {
    ['(3) 8x10', '(8) Wallets', '(2) Magnets', '(1) Class Picture']
      .forEach((label) => expect(isDownloadItem(label)).toBe(false));
  });
});

describe('hasPhysicalItem', () => {
  it('is true for the mixed Star Student checklist (prints + downloads)', () => {
    expect(hasPhysicalItem([
      '(3) 8x10', '(6) 5x7', '(8) 3x5', '(8) Wallets',
      '(2) Magnets', '(11) High-Resolution Downloads', '(1) Class Picture',
    ])).toBe(true);
  });

  it('is false when every row is digital', () => {
    expect(hasPhysicalItem(['(11) High-Resolution Downloads', '(2) Digital Images'])).toBe(false);
  });

  it('is true when a single print sits among downloads', () => {
    expect(hasPhysicalItem(['(11) High-Resolution Downloads', '(1) Class Picture'])).toBe(true);
  });

  it('returns the caller fallback when there are no rows to inspect', () => {
    expect(hasPhysicalItem([], true)).toBe(true);
    expect(hasPhysicalItem([], false)).toBe(false);
  });

  it('defaults the empty case to "it ships" rather than assuming digital', () => {
    expect(hasPhysicalItem([])).toBe(true);
  });
});

describe('parseItem', () => {
  it('parses a "(N) Label" row', () => {
    expect(parseItem('(3) 5x7')).toEqual({ count: 3, label: '5x7' });
  });

  it('doubles "(1) Magnets" to (2) so the card shows the customer-facing count', () => {
    expect(parseItem('(1) Magnets')).toEqual({ count: 2, label: 'Magnet' });
  });

  it('also doubles the singular "(1) Magnet"', () => {
    expect(parseItem('(1) Magnet')).toEqual({ count: 2, label: 'Magnet' });
  });

  it('doubles "(1) Photo Magnet" and drops the qualifier (elementary Star Student)', () => {
    expect(parseItem('(1) Photo Magnet')).toEqual({ count: 2, label: 'Magnet' });
    expect(parseItem('(1) Photo Magnets')).toEqual({ count: 2, label: 'Magnet' });
  });

  it('is case-insensitive on the magnets rule', () => {
    expect(parseItem('(1) magnets')).toEqual({ count: 2, label: 'Magnet' });
    expect(parseItem('(1) MAGNETS')).toEqual({ count: 2, label: 'Magnet' });
    expect(parseItem('(1) PHOTO MAGNET')).toEqual({ count: 2, label: 'Magnet' });
  });

  it('leaves an explicit "(2) Magnets" count alone (admin already asked for two sheets)', () => {
    expect(parseItem('(2) Magnets')).toEqual({ count: 2, label: 'Magnet' });
  });

  it('leaves a SIZED magnet row alone -- "(1) 5x7 Magnet" is one magnet, not a two-up sheet', () => {
    expect(parseItem('(1) 5x7 Magnet')).toEqual({ count: 1, label: '5x7 Magnet' });
    expect(parseItem('(2) 4x5 Magnets')).toEqual({ count: 2, label: '4x5 Magnets' });
  });

  it('leaves other "(1) X" rows unchanged', () => {
    expect(parseItem('(1) 8x10')).toEqual({ count: 1, label: '8x10' });
    expect(parseItem('(1) High-Resolution Download')).toEqual({ count: 1, label: 'High-Resolution Download' });
  });

  it('does NOT match a label that merely contains "magnet" (e.g. "Magnet Frame")', () => {
    expect(parseItem('(1) Magnet Frame')).toEqual({ count: 1, label: 'Magnet Frame' });
  });

  it('treats a free-text row as count 1', () => {
    expect(parseItem('Free-text note')).toEqual({ count: 1, label: 'Free-text note' });
  });
});

describe('collapseItems', () => {
  it('sums repeats and pluralizes when the total is >1', () => {
    expect(collapseItems([
      '(1) High-Resolution Download',
      '(1) High-Resolution Download',
      '(1) High-Resolution Download',
    ])).toEqual(['(3) High-Resolution Downloads']);
  });

  it('merges singular + plural rows under the plural label', () => {
    expect(collapseItems(['(1) Download', '(1) Downloads'])).toEqual(['(2) Downloads']);
  });

  it('does not pluralize size strings that end in a digit', () => {
    expect(collapseItems(['(2) 8x10', '(1) 8x10'])).toEqual(['(3) 8x10']);
  });

  it('applies the magnets-doubling rule before collapsing', () => {
    // Two sheets written as two "(1) Magnets" lines -> 2+2 = 4 individual magnets.
    expect(collapseItems(['(1) Magnets', '(1) Magnets'])).toEqual(['(4) Magnets']);
  });

  it('shows a "(1) Photo Magnet" row as "(2) Magnets"', () => {
    expect(collapseItems(['(1) Photo Magnet'])).toEqual(['(2) Magnets']);
  });

  it('groups the different magnet wordings together', () => {
    // Canonicalizing the label means "Photo Magnet" and "Magnets" share a
    // grouping key: two sheets -> 4 magnets, on one line.
    expect(collapseItems(['(1) Photo Magnet', '(1) Magnets'])).toEqual(['(4) Magnets']);
  });

  it('renders the Star Student checklist the way the card shows it', () => {
    expect(collapseItems([
      '(3) 8x10', '(6) 5x7', '(8) 3x5', '(8) Wallets',
      '(1) Photo Magnet', '(11) High-Resolution Downloads', '(1) Class Picture',
    ])).toEqual([
      '(3) 8x10', '(6) 5x7', '(8) 3x5', '(8) Wallets',
      '(2) Magnets', '(11) High-Resolution Downloads', '(1) Class Picture',
    ]);
  });

  it('preserves order of first appearance', () => {
    expect(collapseItems(['(1) 5x7', '(2) 8x10', '(1) 5x7']))
      .toEqual(['(2) 5x7', '(2) 8x10']);
  });
});

describe('isClassPictureItem', () => {
  it('matches every wording production uses for the row', () => {
    [
      '(1) Class Picture',
      '(1) Complimentary Class Picture',
      '(1) Every Student Gets a Complimentary Class Picture',
      '(1) 10x20 Class Group Picture',
      '(1) CLASS PICTURE',
    ].forEach((label) => expect(isClassPictureItem(label)).toBe(true));
  });

  it('does not match a DIGITAL class picture -- the server calls that a download', () => {
    // ImageType#classname returns 'digital' before it reaches its 'class'
    // test, and HAS_CLASS_PICTURE_SQL excludes the same rows. Calling one of
    // these printed would contradict what actually ships.
    expect(isClassPictureItem('(1) Digital Class Picture')).toBe(false);
  });

  it('does not match a yearbook row', () => {
    expect(isClassPictureItem('(1) Yearbook Class Pose')).toBe(false);
  });

  it('does not match ordinary prints', () => {
    ['(3) 8x10', '(8) Wallets', '(2) Magnets', '(1) High-Resolution Download']
      .forEach((label) => expect(isClassPictureItem(label)).toBe(false));
  });
});

describe('classPictureIsTheOnlyPrint', () => {
  it('is true for the #877 All Digital Package checklist', () => {
    expect(classPictureIsTheOnlyPrint([
      '(12) High-Resolution Downloads', '(1) Class Picture',
    ])).toBe(true);
  });

  it('is true for the #877 Starter checklist', () => {
    expect(classPictureIsTheOnlyPrint([
      '(1) High-Resolution Download', '(1) Class Picture',
    ])).toBe(true);
  });

  it('is false once the card prints anything else', () => {
    expect(classPictureIsTheOnlyPrint([
      '(3) 8x10', '(8) Wallets',
      '(11) High-Resolution Downloads', '(1) Class Picture',
    ])).toBe(false);
  });

  it('is false for a card with no class picture at all', () => {
    expect(classPictureIsTheOnlyPrint(['(11) High-Resolution Downloads'])).toBe(false);
  });

  it('is false when nothing on the card is a download', () => {
    // The legacy class-picture-only options: nothing to be mistaken for, so
    // nothing to say. Keeps the change to the cards that confuse parents.
    expect(classPictureIsTheOnlyPrint(['(1) Class Picture'])).toBe(false);
  });

  it('is false for an empty checklist', () => {
    expect(classPictureIsTheOnlyPrint([])).toBe(false);
  });

  it('does not treat a digital class picture as the print', () => {
    expect(classPictureIsTheOnlyPrint([
      '(11) High-Resolution Downloads', '(1) Digital Class Picture',
    ])).toBe(false);
  });
});
